import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { MachineContext } from '../DataContexts/MachineContext';
import { MachineGroupContext } from '../DataContexts/MachineGroupContext';
import { ConfigurationContext } from '../DataContexts/ConfigurationContext';
import { machineDetails, filterAndSortMachines } from '../PlaylistManager/PlaylistAssign';
import DashboardSection from '../components/DashboardSection';
import useDeepLinkedSection from '../navigation/useDeepLinkedSection';
import '../PlaylistManager/PlaylistAssign.css';
import './ConfigSubscriptions.css';

// DJB-179: one Playlist-Subscriptions-style section per config type. The
// model lists come from the page-scoped ConfigurationContext — the same store
// the Configuration sections create into — so a freshly created model shows
// up here without a page reload. Assignment still loops the per-machine
// endpoints on MachineContext (there is no bulk endpoint; groups expand
// client-side, DJB-133/166). `featuresKey` maps a machine's current model out
// of the bundled optional-features cache.
export const CONFIG_TYPES = [
  {
    key: 'payment',
    title: 'Payment Mode Subscriptions',
    label: 'payment mode',
    anchor: 'section-config-payment-subscriptions',
    info: 'Wijs een betaalmodel toe aan machines en groepen.',
    modelsKey: 'paymentModes',
    loadingKey: 'paymentModesLoading',
    fetchKey: 'fetchPaymentModes',
    assignKey: 'updatePaymentMode',
    featuresKey: 'paymentModeId',
  },
  {
    key: 'bonus',
    title: 'Bonus Configuration Subscriptions',
    label: 'bonus configuration',
    anchor: 'section-config-bonus-subscriptions',
    info: 'Wijs een bonusconfiguratie toe aan machines en groepen.',
    modelsKey: 'bonusConfigurations',
    loadingKey: 'bonusConfigurationsLoading',
    fetchKey: 'fetchBonusConfigurations',
    assignKey: 'updateBonusConfiguration',
    featuresKey: 'bonusId',
  },
  {
    key: 'longtrack',
    title: 'Long-Track Price Subscriptions',
    label: 'long-track price model',
    anchor: 'section-config-longtrack-subscriptions',
    info: 'Wijs een long-track prijsmodel toe aan machines en groepen.',
    modelsKey: 'longTrackModes',
    loadingKey: 'longTrackModesLoading',
    fetchKey: 'fetchLongTrackModes',
    assignKey: 'updateLongTrackPriceMode',
    featuresKey: 'longTrackPriceMode',
  },
  {
    key: 'disk',
    title: 'On-Disk Policy Subscriptions',
    label: 'on-disk policy',
    anchor: 'section-config-disk-subscriptions',
    info: 'Wijs een on-disk policy toe aan machines en groepen.',
    modelsKey: 'diskPolicies',
    loadingKey: 'diskPoliciesLoading',
    fetchKey: 'fetchDiskPolicies',
    assignKey: 'updateDiskPolicy',
    featuresKey: 'diskPolicyId',
  },
];

// One assignment section, laid out like Playlist Subscriptions: models on the
// left, machines on the selected model in the middle, the other machines and
// the machine groups on the right with per-row assign actions.
const ConfigTypeSection = ({ type, active, onToggle }) => {
  const machineContext = useContext(MachineContext);
  const {
    machines,
    optionalFeatures,
    loading: machinesLoading,
    error: machinesError,
  } = machineContext;
  const assignModel = machineContext[type.assignKey];
  const { machineGroups, loading: machineGroupsLoading, error: machineGroupsError } =
    useContext(MachineGroupContext);
  const configContext = useContext(ConfigurationContext);
  const modelsFromContext = configContext[type.modelsKey];
  const models = useMemo(() => modelsFromContext ?? [], [modelsFromContext]);
  const modelsLoading = configContext[type.loadingKey];
  const fetchModels = configContext[type.fetchKey];

  const [selectedModelId, setSelectedModelId] = useState(null);
  const [modelSearch, setModelSearch] = useState('');
  const [subscribedSearch, setSubscribedSearch] = useState('');
  const [subscribedSortKey, setSubscribedSortKey] = useState('name');
  const [subscribedSortAsc, setSubscribedSortAsc] = useState(true);
  const [availableSearch, setAvailableSearch] = useState('');
  const [availableSortKey, setAvailableSortKey] = useState('name');
  const [availableSortAsc, setAvailableSortAsc] = useState(true);
  const [groupSearch, setGroupSearch] = useState('');
  // machineId -> modelId recorded on successful assigns this session,
  // overlaid on the optional-features cache so a swap shows immediately
  // without waiting for the cache to refresh.
  const [sessionAssignments, setSessionAssignments] = useState({});
  // null | { status: 'running' | 'done', done, total, failures, modelName }
  const [assignRun, setAssignRun] = useState(null);
  const running = assignRun?.status === 'running';

  // DJB-179 bug fix, second half: refetch this type's model list every time
  // the section is opened, so models created elsewhere (or by another client)
  // are picked up on section focus without a reload.
  const fetchModelsRef = useRef(fetchModels);
  fetchModelsRef.current = fetchModels;
  useEffect(() => {
    if (active) fetchModelsRef.current?.();
  }, [active]);

  const selectedModel = useMemo(
    () => models.find((m) => m.id === selectedModelId) ?? null,
    [models, selectedModelId]
  );

  const filteredModels = useMemo(() => {
    const q = modelSearch.toLowerCase().trim();
    if (!q) return models;
    return models.filter((m) => String(m.name ?? '').toLowerCase().includes(q));
  }, [models, modelSearch]);

  // machineId -> currently assigned model id: the bundled optional-features
  // cache, overlaid with the assignments made in this session.
  const currentModelByMachine = useMemo(() => {
    const map = {};
    if (type.featuresKey) {
      optionalFeatures?.forEach((features) => {
        if (features.machineId && features[type.featuresKey]) {
          map[features.machineId] = features[type.featuresKey];
        }
      });
    }
    return { ...map, ...sessionAssignments };
  }, [optionalFeatures, type.featuresKey, sessionAssignments]);

  const modelNameById = useMemo(() => {
    const map = {};
    models.forEach((m) => {
      map[m.id] = m.name ?? m.id;
    });
    return map;
  }, [models]);

  // modelId -> number of machines currently on it (badge in the model list)
  const assignedCounts = useMemo(() => {
    const counts = {};
    Object.values(currentModelByMachine).forEach((modelId) => {
      counts[modelId] = (counts[modelId] ?? 0) + 1;
    });
    return counts;
  }, [currentModelByMachine]);

  const machinesWithDetails = useMemo(
    () => (machines ?? []).map(machineDetails),
    [machines]
  );

  const subscribedMachines = useMemo(() => {
    if (!selectedModelId) return [];
    return machinesWithDetails.filter((m) => currentModelByMachine[m.id] === selectedModelId);
  }, [machinesWithDetails, currentModelByMachine, selectedModelId]);

  const availableMachines = useMemo(() => {
    if (!selectedModelId) return [];
    return machinesWithDetails.filter((m) => currentModelByMachine[m.id] !== selectedModelId);
  }, [machinesWithDetails, currentModelByMachine, selectedModelId]);

  const filteredSubscribed = useMemo(
    () => filterAndSortMachines(subscribedMachines, subscribedSearch, subscribedSortKey, subscribedSortAsc),
    [subscribedMachines, subscribedSearch, subscribedSortKey, subscribedSortAsc]
  );

  const filteredAvailable = useMemo(
    () => filterAndSortMachines(availableMachines, availableSearch, availableSortKey, availableSortAsc),
    [availableMachines, availableSearch, availableSortKey, availableSortAsc]
  );

  const filteredGroups = useMemo(() => {
    if (!machineGroups?.length) return [];
    const q = groupSearch.toLowerCase().trim();
    if (!q) return machineGroups;
    return machineGroups.filter((g) => g.name?.toLowerCase().includes(q));
  }, [machineGroups, groupSearch]);

  const machineNameById = useMemo(() => {
    const map = {};
    machinesWithDetails.forEach((m) => {
      map[m.id] = m.name;
    });
    return map;
  }, [machinesWithDetails]);

  const selectModel = (modelId) => {
    if (running) return;
    setSelectedModelId(modelId);
    setAssignRun(null);
  };

  const toggleSubscribedSort = (key) => {
    if (subscribedSortKey === key) {
      setSubscribedSortAsc((a) => !a);
    } else {
      setSubscribedSortKey(key);
      setSubscribedSortAsc(true);
    }
  };

  const toggleAvailableSort = (key) => {
    if (availableSortKey === key) {
      setAvailableSortAsc((a) => !a);
    } else {
      setAvailableSortKey(key);
      setAvailableSortAsc(true);
    }
  };

  // Non-atomic by design: one POST per machine, continuing past failures so
  // one broken machine doesn't stop the rest of the batch (DJB-166). The run
  // panel aggregates the result instead of a toast per machine.
  const runAssign = async (machineIds) => {
    const targets = [...new Set(machineIds)];
    if (!selectedModel || targets.length === 0 || running) return;
    const run = {
      status: 'running',
      done: 0,
      total: targets.length,
      failures: [],
      modelName: selectedModel.name ?? selectedModel.id,
    };
    setAssignRun({ ...run });
    for (const machineId of targets) {
      try {
        await assignModel(machineId, selectedModel.id, { notify: false });
        setSessionAssignments((prev) => ({ ...prev, [machineId]: selectedModel.id }));
      } catch (e) {
        run.failures.push({
          machineId,
          name: machineNameById[machineId] ?? machineId,
          message: e.message,
        });
      }
      run.done += 1;
      setAssignRun({ ...run, failures: [...run.failures] });
    }
    run.status = 'done';
    setAssignRun({ ...run, failures: [...run.failures] });
  };

  const handleAssignMachine = (machineId) => runAssign([machineId]);

  const handleAssignAll = () => runAssign(filteredAvailable.map((m) => m.id));

  // Groups expand to their member machines client-side; members already on
  // the selected model are skipped so each machine is POSTed at most once
  const handleAssignGroup = (group) =>
    runAssign(
      (group.machineIds ?? []).filter((machineId) => currentModelByMachine[machineId] !== selectedModel?.id)
    );

  const sortArrow = (isActive, asc) =>
    isActive ? <span className="ps-sort-arrow">{asc ? '▲' : '▼'}</span> : null;

  const sectionShell = (children) => (
    <DashboardSection
      id={type.anchor}
      title={type.title}
      info={type.info}
      isOpen={active}
      onToggle={onToggle}
      bodyId={`cs-body-${type.key}`}
    >
      {children}
    </DashboardSection>
  );

  if (machinesLoading || machineGroupsLoading || modelsLoading) {
    return sectionShell(<p className="ps-status">Loading data, please wait...</p>);
  }

  if (machinesError || machineGroupsError) {
    return sectionShell(
      <p className="ps-status ps-status--error">
        Error loading data: {machinesError || machineGroupsError}
      </p>
    );
  }

  const succeeded = assignRun ? assignRun.done - assignRun.failures.length : 0;

  return sectionShell(
    <div className="ps-columns">
      {/* First column: search and list of models of this config type */}
      <div className="ps-column ps-column--models">
        <div className="ps-column-head">
          <span className="ps-column-label">Models</span>
          <span className="ps-column-count">{filteredModels.length}</span>
        </div>
        <div className="ps-filters">
          <input
            type="text"
            className="ps-input"
            placeholder="Search by model name..."
            value={modelSearch}
            onChange={(e) => setModelSearch(e.target.value)}
          />
        </div>
        <div className="ps-list-scroll">
          <ul className="ps-playlist-list">
            {models.length === 0 && <li className="ps-empty">No models</li>}
            {models.length > 0 && filteredModels.length === 0 && (
              <li className="ps-empty">No matching models</li>
            )}
            {filteredModels.map((model) => (
              <li
                key={model.id}
                className={`ps-playlist-item${selectedModelId === model.id ? ' selected' : ''}`}
                onClick={() => selectModel(model.id)}
              >
                <div className="ps-playlist-row">
                  <span className="ps-playlist-name">{model.name ?? model.id}</span>
                  {type.featuresKey && (
                    <span className="ps-subs-badge">
                      {assignedCounts[model.id] ?? 0} machine{(assignedCounts[model.id] ?? 0) === 1 ? '' : 's'}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
      {/* Second column: selected model info, machines on it, run result */}
      <div className="ps-column ps-column--subscribed">
        {selectedModel ? (
          <>
            <div className="ps-selected">
              <div className="ps-selected-name">{selectedModel.name ?? selectedModel.id}</div>
              <div className="ps-selected-id">
                Model id: <span>{selectedModel.id}</span>
              </div>
            </div>
            <div className="ps-column-head">
              <h3 className="ps-column-label">Subscribed machines</h3>
              <span className="ps-column-count">{subscribedMachines.length}</span>
            </div>
            <div className="ps-toolbar">
              <input
                type="text"
                className="ps-input"
                placeholder="Search by name, place, post, province..."
                value={subscribedSearch}
                onChange={(e) => setSubscribedSearch(e.target.value)}
              />
            </div>
            <div className="ps-table-scroll">
              <table className="ps-table">
                <thead>
                  <tr>
                    <th className="ps-th-sort" onClick={() => toggleSubscribedSort('name')}>
                      Name {sortArrow(subscribedSortKey === 'name', subscribedSortAsc)}
                    </th>
                    <th className="ps-th-sort" onClick={() => toggleSubscribedSort('place')}>
                      Place {sortArrow(subscribedSortKey === 'place', subscribedSortAsc)}
                    </th>
                    <th className="ps-th-sort" onClick={() => toggleSubscribedSort('post')}>
                      Post {sortArrow(subscribedSortKey === 'post', subscribedSortAsc)}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSubscribed.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="ps-empty-cell">
                        {subscribedMachines.length === 0 ? 'None' : 'No matching machines'}
                      </td>
                    </tr>
                  ) : (
                    filteredSubscribed.map((m) => (
                      <tr key={m.id}>
                        <td>{m.name}</td>
                        <td>{m.place}</td>
                        <td>{m.post}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {assignRun && (
              <div className="cs-assign-result" role="status">
                <div className="cs-progress" aria-hidden="true">
                  <div
                    className="cs-progress-fill"
                    style={{ width: `${assignRun.total ? Math.round((assignRun.done / assignRun.total) * 100) : 0}%` }}
                  />
                </div>
                <p className="cs-progress-text">
                  {assignRun.status === 'running'
                    ? `Assigning ${assignRun.modelName}… ${assignRun.done}/${assignRun.total}`
                    : assignRun.failures.length === 0
                      ? `Assigned ${assignRun.modelName} to ${succeeded} machine${succeeded === 1 ? '' : 's'}.`
                      : `Assigned ${assignRun.modelName} to ${succeeded} of ${assignRun.total} machines — ${assignRun.failures.length} failed.`}
                </p>
                {assignRun.failures.length > 0 && (
                  <ul className="cs-failure-list">
                    {assignRun.failures.map((failure) => (
                      <li key={failure.machineId} className="cs-failure-item">
                        <span className="cs-failure-name">{failure.name}</span>
                        <span className="cs-failure-message">{failure.message}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="ps-placeholder">Select a {type.label} to see details</div>
        )}
      </div>
      {/* Third column: the other machines and the machine groups */}
      <div className="ps-column ps-column--available">
        {!selectedModel ? (
          <div className="ps-placeholder">Select a {type.label} to see details</div>
        ) : (
          <>
            <div className="ps-column-head">
              <h3 className="ps-column-label">Other machines</h3>
              <span className="ps-column-count">{availableMachines.length}</span>
            </div>
            <div className="ps-toolbar">
              <input
                type="text"
                className="ps-input"
                placeholder="Search by name, place, post, province..."
                value={availableSearch}
                onChange={(e) => setAvailableSearch(e.target.value)}
              />
              <button
                type="button"
                className="ps-btn ps-btn--success"
                title="Move every listed machine to this model"
                onClick={handleAssignAll}
                disabled={running || filteredAvailable.length === 0}
              >
                Move all here
              </button>
            </div>
            <div className="ps-table-scroll ps-table-scroll--available">
              <table className="ps-table">
                <thead>
                  <tr>
                    <th className="ps-th-sort" onClick={() => toggleAvailableSort('name')}>
                      Name {sortArrow(availableSortKey === 'name', availableSortAsc)}
                    </th>
                    <th className="ps-th-sort" onClick={() => toggleAvailableSort('place')}>
                      Place {sortArrow(availableSortKey === 'place', availableSortAsc)}
                    </th>
                    <th className="ps-th-sort" onClick={() => toggleAvailableSort('post')}>
                      Post {sortArrow(availableSortKey === 'post', availableSortAsc)}
                    </th>
                    {type.featuresKey && <th>Current model</th>}
                    <th className="ps-td-action"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAvailable.length === 0 ? (
                    <tr>
                      <td colSpan={type.featuresKey ? 5 : 4} className="ps-empty-cell">
                        {availableMachines.length === 0
                          ? machinesWithDetails.length === 0
                            ? 'No machines'
                            : 'All machines subscribed'
                          : 'No matching machines'}
                      </td>
                    </tr>
                  ) : (
                    filteredAvailable.map((m) => (
                      <tr key={m.id}>
                        <td>{m.name}</td>
                        <td>{m.place}</td>
                        <td>{m.post}</td>
                        {type.featuresKey && (
                          <td className="cs-td-current">
                            {currentModelByMachine[m.id]
                              ? modelNameById[currentModelByMachine[m.id]] ?? currentModelByMachine[m.id]
                              : '—'}
                          </td>
                        )}
                        <td className="ps-td-action">
                          <button
                            className="ps-icon-btn ps-icon-btn--add"
                            title="Move here (swaps from current model)"
                            aria-label={`Move ${m.name} to this model`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAssignMachine(m.id);
                            }}
                            disabled={running}
                          >
                            +
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="ps-column-head ps-column-head--groups">
              <h3 className="ps-column-label">Machine groups</h3>
              <span className="ps-column-count">{filteredGroups.length}</span>
            </div>
            <div className="ps-toolbar">
              <input
                type="text"
                className="ps-input"
                placeholder="Search by group name..."
                value={groupSearch}
                onChange={(e) => setGroupSearch(e.target.value)}
              />
            </div>
            <div className="ps-table-scroll ps-table-scroll--groups">
              <table className="ps-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Machines</th>
                    <th className="ps-td-action"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredGroups.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="ps-empty-cell">
                        {!machineGroups?.length ? 'No groups' : 'No matching groups'}
                      </td>
                    </tr>
                  ) : (
                    filteredGroups.map((group) => (
                      <tr key={group.id}>
                        <td>{group.name}</td>
                        <td>{group.machineIds?.length ?? 0}</td>
                        <td className="ps-td-action">
                          <button
                            className="ps-icon-btn ps-icon-btn--add"
                            title="Move every machine in the group to this model"
                            aria-label={`Move group ${group.name} to this model`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAssignGroup(group);
                            }}
                            disabled={running || !group.machineIds?.length}
                          >
                            +
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// DJB-179: Config Subscriptions — a distinct section per config type, in the
// Playlist Subscriptions design. Sections open/close independently and start
// collapsed with a muted info line, the DJB-83 behaviour the Curator
// subscription sections use.
const ConfigSubscriptions = () => {
  const [openSections, setOpenSections] = useState(() => new Set());
  const toggleSection = (key) =>
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });

  // DJB-112: a section picked from the left nav arrives as ?section=<anchor>
  // and must open even though sections default to collapsed
  useDeepLinkedSection(
    Object.fromEntries(CONFIG_TYPES.map((type) => [type.anchor, type.key])),
    (key) => setOpenSections((prev) => (prev.has(key) ? prev : new Set(prev).add(key)))
  );

  return (
    <div className="ps-page config-subscriptions-page">
      {CONFIG_TYPES.map((type) => (
        <ConfigTypeSection
          key={type.key}
          type={type}
          active={openSections.has(type.key)}
          onToggle={() => toggleSection(type.key)}
        />
      ))}
    </div>
  );
};

export default ConfigSubscriptions;
